import { createPinia, defineStore, setActivePinia } from "pinia";
import type { AxiosResponse } from "axios";
import type { Category } from "@/types/category";
import type { State } from "@/types/store";
import type { Sorting } from "@/types/sorting";
import type { Submitted } from "@/types/submitted";
import type { ResultItem } from "@/types/resultItem";
import type { SearchResult } from "@/types/searchResult";
import type { Teaser } from "@/types/teaser";
import type { SearchParams } from "@/lib/url/pushUrlSearchParams";
import {
  addSearchParams,
  deleteSearchParam,
  getCurrentSearchParams,
  pushUrlSearchParams,
} from "@/lib/url/pushUrlSearchParams";
import { apiClient, initApiClient } from "@/services/services";
import type { DateRange } from "@/types/dateRange";
import { submittedToUrlParamsTranslation } from "@/globalConsts/submittedToUrlParamTranslation";
import { fetchResults } from "@/services/fetchResults";
import { sortCategories } from "@/lib/sorting/sortCategories";
import { createSubmittedFromUrl } from "@/lib/url/createSubmittedFromUrl";
import { PAGE, SCONTENT } from "@/globalConsts/queryParams";
import { resizeIFrame } from "@/lib/iFrame/iframeResizer";
import { isTeaser } from "@/types/teaser";
import { searchTerm } from "@/globalConsts/searchBarItems";
import { durationMax, priceMax } from "@/globalConsts/rangeFilter";
import { fetchDestinations } from "@/services/fetchDestinations";
import { pushToDataLayer } from "@/lib/gtm/dataLayer";
import { lookUp } from "@/services/lookupDestinations";
import { mapLocaleToCurrency } from "@/services/localeCurrencyMapper";

setActivePinia(createPinia());

export const useSearchStore = defineStore("search", {
  state: () => {
    return <State>{
      configuration: {
        apiEndpoint: "",
        ident: "",
        isWhitelabel: false,
        isIframe: false,
        itemsPerPage: parseInt(import.meta.env.VITE_ITEMS_PER_PAGE ?? 24),
        pricesPerPage: 5,
        currency: "Eur",
        backendApiEndpoint: "",
        internalApi: "",
        locale: "",
      },
      categories: [],
      countryTags: [],
      sorting: [],
      price: {
        min: 0,
        max: 0,
      },
      duration: {
        min: 0,
        max: 0,
      },
      submitted: {
        tags: [],
        travelNumber: "",
      },
      filter: {
        categories: [],
      },
      results: {
        items: [],
        maxResultCountExceeded: true,
        total: 0,
      },
      quickSearchResult: [],
      teaser: null,
      page: 1,
      seaContent: "",
      isInitialized: false,
      searchBarIsOpen: false,
      enteredSearchTerm: "",
      isPalmDevice: true,
      openRestoreModal: false,
    };
  },

  actions: {
    initSearch(
      apiEndpoint: string,
      ident: string,
      isWhitelabel: boolean,
      isIframe: boolean,
      backendApiEndpoint: string,
      internalApi: string,
      locale: string
    ) {
      this.configuration.apiEndpoint = apiEndpoint;
      this.configuration.ident = ident;
      this.configuration.isWhitelabel = isWhitelabel;
      this.configuration.isIframe = isIframe;
      this.configuration.backendApiEndpoint = backendApiEndpoint;
      this.configuration.internalApi = internalApi;
      this.configuration.locale = locale;
      this.configuration.currency = mapLocaleToCurrency(locale);

      initApiClient(this.configuration.ident);
      this.initSubmitted();
    },

    async initOptions(): Promise<void> {
      if (this.isInitialized) return;

      try {
        const response: AxiosResponse = await apiClient.get(
          this.configuration.apiEndpoint + "options"
        );
        this.sorting = response.data.sorting;
        this.price = response.data.price;
        this.duration = response.data.durationRange;
        sortCategories(response.data.categories);
        this.categories = response.data.categories;
        this.countryTags = response.data.countryTags;

        this.filter.categories = this.categories;

        this.isInitialized = true;
      } catch (e) {
        if (e instanceof Error) {
          console.error("options " + e.message);
        }
      }
    },

    async setResults(): Promise<void> {
      this.pushUrlParams();
      const results:
        | {
            resultItems: ResultItem[];
            filter: Category[];
            total: number;
            maxResultCountExceeded: boolean;
          }
        | undefined = await fetchResults(
        this.configuration.apiEndpoint,
        this.submitted,
        this.categories,
        this.configuration.itemsPerPage,
        this.countryTags
      );

      resizeIFrame();

      if (!results) {
        return;
      }
      this.filter.categories =
        results.filter.length > 0 ? results.filter : this.categories;
      this.results.items = results.resultItems;
      this.results.total = results.total ?? 0;
      this.results.maxResultCountExceeded = results.maxResultCountExceeded;
    },

    async saveSubmittedFilter(type: string, value: string): Promise<void> {
      this.resetPagination();

      if (type === "tags") {
        if (this.submitted.tags && this.submitted.tags.includes(value)) {
          this.resetTag(value);
        } else {
          this.updateTag(value);
        }
      } else {
        //todo: find a better solution NSDJW-5858
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        this.submitted[type as keyof Submitted] = value;
      }

      await this.setResults();
    },
    async saveSubmittedTravelNumber(travelNumber: string): Promise<void> {
      this.submitted.travelNumber = travelNumber
        .trim()
        .toUpperCase()
        .replace("JA", "");

      await this.setResults();
    },

    async saveSubmittedPriceRatio(
      minPrice: number,
      maxPrice: number
    ): Promise<void> {
      this.resetPagination();

      if (minPrice === this.price.min && maxPrice === priceMax) {
        delete this.submitted.minPrice;
        delete this.submitted.maxPrice;
      } else {
        this.submitted.minPrice = minPrice;
        this.submitted.maxPrice =
          maxPrice < priceMax ? maxPrice : this.price.max;
      }
      await this.setResults();
    },

    saveSubmittedDurationRatio(minDuration: number, maxDuration: number): void {
      this.resetPagination();
      if (minDuration === this.duration.min && maxDuration === durationMax) {
        delete this.submitted.minDuration;
        delete this.submitted.maxDuration;
      } else {
        this.submitted.minDuration = minDuration;
        this.submitted.maxDuration =
          maxDuration < durationMax ? maxDuration : this.duration.max;
      }
    },

    async saveSubmittedPagination(page: number): Promise<void> {
      this.submitted.pageFrom = this.configuration.itemsPerPage * (page - 1);
      await this.setResults();
    },

    resetPagination() {
      this.submitted.pageFrom = 0;
      this.page = 1;
    },

    pushUrlParams(): void {
      let searchParams: SearchParams = getCurrentSearchParams();

      Object.keys(submittedToUrlParamsTranslation).forEach((key) => {
        if (key === "price") {
          searchParams = addSearchParams(
            key,
            searchParams,
            this.submitted.minPrice,
            this.submitted.maxPrice
          );
        } else if (key === "duration") {
          searchParams = addSearchParams(
            key,
            searchParams,
            this.submitted.minDuration,
            this.submitted.maxDuration,
            "-"
          );
        } else {
          const searchParamValue = this.submitted[key as keyof Submitted];
          const convertedParamKey = submittedToUrlParamsTranslation[key];

          if (convertedParamKey !== undefined) {
            searchParams[convertedParamKey] = searchParamValue
              ? searchParamValue.toString()
              : "";
          }
        }
      });

      searchParams[PAGE] = this.page.toString();

      pushUrlSearchParams(searchParams);
    },

    initSubmitted() {
      const submitted:
        | { submittedValues: Submitted; currentPage: number; restore: boolean }
        | undefined = createSubmittedFromUrl(this.configuration.itemsPerPage);

      if (submitted) {
        this.submitted = submitted.submittedValues;
        this.page = submitted.currentPage;
        this.openRestoreModal = submitted.restore;
      }
    },

    async initSearchResult(): Promise<void> {
      const destinations = await fetchDestinations(
        this.configuration.internalApi
      );

      if (destinations) {
        this.setInitialSearchResult(destinations);
        this.cacheSearchTerm(destinations);
      }
    },

    setInitialSearchResult(destinations: string): void {
      const searchResult: SearchResult = {
        filter: "inital",
        value: destinations,
      };

      this.quickSearchResult = [searchResult];
    },

    resetTag(value: string): void {
      if (!this.submitted.tags) {
        return;
      }

      const valuePosition = this.submitted.tags.indexOf(value);

      this.submitted.tags.splice(valuePosition, 1);

      if (this.quickSearchResult?.some((item) => item.filter === value)) {
        this.updateSearchTerm(value);
      }
    },

    updateTag(value: string) {
      Array.isArray(this.submitted.tags)
        ? this.submitted.tags.push(value)
        : (this.submitted.tags = [value]);
    },

    async quickSearch(searchValue: string, setResults = true): Promise<void> {
      let newSearchTerm: SearchResult[] = [];
      if (this.quickSearchResult.length > 0) {
        this.resetQuickSearchResults();
      }
      this.resetFilter();

      if (searchValue) {
        try {
          newSearchTerm = await lookUp(
            this.configuration.backendApiEndpoint,
            searchValue,
            this.configuration.locale
          );

          newSearchTerm.forEach((item: SearchResult) => {
            if (item.filter) {
              this.updateTag(item.filter);
            }
          });
        } catch (e) {
          if (e instanceof Error) {
            console.error("searchTerm " + e.message);
          }
        }
        this.quickSearchResult = newSearchTerm;
      }

      if (setResults) {
        await this.setResults();
      }
    },

    async quickSearchFromStore() {
      let newSearchTerm: SearchResult[] = [];

      if (this.enteredSearchTerm === this.getQuickSearchValue) {
        return;
      }

      if (this.quickSearchResult.length > 0) {
        this.resetQuickSearchResults();
      }
      this.resetFilter();

      if (this.enteredSearchTerm) {
        try {
          newSearchTerm = await lookUp(
            this.configuration.backendApiEndpoint,
            this.enteredSearchTerm,
            this.configuration.locale
          );
          pushToDataLayer({ event: "search", search_term: newSearchTerm });

          newSearchTerm.forEach((item: SearchResult) => {
            if (item.filter) {
              this.updateTag(item.filter);
            }
          });
        } catch (e) {
          if (e instanceof Error) {
            console.error("searchTerm " + e.message);
          }
        }

        this.quickSearchResult = newSearchTerm;
        this.cacheSearchTerm(this.getQuickSearchValue);
      }
    },

    cacheSearchTerm(value: string): void {
      this.enteredSearchTerm = value;
    },

    resetCacheSearchTerm(): void {
      this.enteredSearchTerm = "";
    },

    resetQuickSearchResults(): void {
      this.quickSearchResult.forEach((result) => {
        this.resetTag(result.filter);
      });
      this.quickSearchResult = [];
    },

    updateSearchTerm(filterValue: string): void {
      this.quickSearchResult = this.quickSearchResult?.filter(
        (item) => item.filter !== filterValue
      );
    },

    resetFilter(): void {
      this.submitted.tags = [];
      delete this.submitted.maxPrice;
      delete this.submitted.minPrice;
      delete this.submitted.travelNumber;
      delete this.submitted.sortBy;
      this.resetPagination();
    },

    async initTeaser(): Promise<void> {
      try {
        const seaContent =
          new URLSearchParams(window.location.search).get(SCONTENT) || "";

        if (seaContent !== "") {
          this.setSeaContent(seaContent);
          const response: AxiosResponse = await apiClient.get(
            this.configuration.internalApi + "teaser?ident=" + seaContent
          );

          if (isTeaser(response.data.response)) {
            this.teaser = response.data.response;
          }
        }
      } catch (e) {
        console.error(e);
      }
    },

    setSeaContent(seaContent: string): void {
      this.seaContent = seaContent;
    },

    resetSeaContentAndTeaser(): void {
      if (this.seaContent === "" || this.teaser === null) {
        return;
      }

      this.teaser = null;
      this.seaContent = "";
      deleteSearchParam(SCONTENT);
    },

    updateSelectedDateRange(dateRange: DateRange) {
      if (Object.keys(dateRange).length < 0) {
        delete this.submitted.startDate;
        delete this.submitted.endDate;
      } else {
        this.submitted.startDate = dateRange.start;
        this.submitted.endDate = dateRange.end;
      }
    },

    async openSearchBar(activeState: boolean): Promise<void> {
      this.searchBarIsOpen = activeState;
      if (activeState) {
        return;
      }

      await this.quickSearchFromStore();
      await this.setResults();
    },

    resetSearchBarParameter(parameter: string): void {
      if (parameter === "duration") {
        this.saveSubmittedDurationRatio(this.duration.min, durationMax);
        return;
      }
      if (parameter === "dateRange") {
        this.updateSelectedDateRange({});
        return;
      }
      if (parameter === searchTerm) {
        this.resetQuickSearchResults();
        this.resetFilter();
      }
    },
    isAPalmDevice(isPalm: boolean) {
      this.isPalmDevice = isPalm;
    },
    closeRestoreModal() {
      this.openRestoreModal = false;
    },
  },

  getters: {
    getFilterItems(state: State): Category[] {
      return state.filter.categories;
    },

    getSortingFilterValues(state: State): Sorting[] {
      return state.sorting;
    },

    getSubmitted(state: State): Submitted {
      return state.submitted;
    },

    getSubmittedTags(state: State): string[] {
      return state.submitted.tags ? state.submitted.tags : [];
    },

    getResultItems(state: State): ResultItem[] {
      return state.results.items;
    },

    getSpecificSubmittedItem: (state) => (item: string) => {
      return state.submitted[item as keyof Submitted]
        ? state.submitted[item as keyof Submitted]
        : "";
    },

    getPriceFilter(state: State) {
      return state.price;
    },

    getPriceRange(state: State): number[] {
      return [state.price.min, priceMax];
    },

    getDurationRange(state: State): number[] {
      return [state.duration.min, durationMax];
    },

    getCurrentPriceRange(state: State): number[] {
      if (state.submitted.minPrice && state.submitted.maxPrice) {
        return state.submitted.maxPrice < priceMax
          ? [state.submitted.minPrice, state.submitted.maxPrice]
          : [state.submitted.minPrice, priceMax];
      }
      return [state.price.min, priceMax];
    },

    getCurrentDurationRange(state: State): number[] {
      if (state.submitted.minDuration && state.submitted.maxDuration) {
        return state.submitted.maxDuration < durationMax
          ? [state.submitted.minDuration, state.submitted.maxDuration]
          : [state.submitted.minDuration, durationMax];
      }
      return [state.duration.min, durationMax];
    },

    getTotalResults(state: State): number {
      return state.results.total;
    },

    getPageCount(state: State): number {
      return Math.ceil(state.results.total / state.configuration.itemsPerPage);
    },

    isMaxResultCountExceeded(state: State): boolean {
      return state.results.maxResultCountExceeded;
    },

    getQuickSearchValue(state: State): string {
      const searchValues: string[] = [];

      state.quickSearchResult?.forEach((item) => {
        if (item.filter) {
          searchValues.push(item.value);
        }
      });

      return searchValues.join(", ");
    },

    getTeaser(state: State): Teaser | null {
      return state.teaser;
    },

    hasTeaser(state: State): boolean {
      return (
        state.teaser !== null &&
        state.teaser.image.path !== "" &&
        state.teaser.imageMobile.path !== ""
      );
    },

    isIframe(state: State): boolean {
      return state.configuration.isIframe;
    },

    getDateRange(state: State): DateRange {
      const dateRange: DateRange = {};
      if (state.submitted.startDate && state.submitted.endDate) {
        dateRange.start = state.submitted.startDate;
        dateRange.end = state.submitted.endDate;
      }

      return dateRange;
    },

    getDateRangeTerm(state: State): string {
      if (!state.submitted.startDate || !state.submitted.endDate) {
        return "";
      }

      const startDate = new Date(state.submitted.startDate).toLocaleString(
        state.configuration.locale,
        { day: "numeric", month: "short" }
      );
      const endDate = new Date(state.submitted.endDate).toLocaleString(
        state.configuration.locale,
        { day: "numeric", month: "short" }
      );

      return startDate + " - " + endDate;
    },

    getDurationTerm(state: State): string {
      let durationTerm = "";

      if (!state.submitted.maxDuration || !state.submitted.minDuration) {
        return durationTerm;
      }

      if (state.submitted.maxDuration < durationMax) {
        durationTerm = `${state.submitted.minDuration} - ${state.submitted.maxDuration} Tage`;
      } else {
        durationTerm =
          state.duration.min < state.submitted.minDuration
            ? `${state.submitted.minDuration} - ${durationMax}+ Tage`
            : "";
      }

      return durationTerm;
    },

    currentSearchItems(): { [index: string]: string } {
      return {
        searchTerm: this.getQuickSearchValue,
        dateRange: this.getDateRangeTerm,
        duration: this.getDurationTerm,
      };
    },

    currentSearchItemValues(): {
      [index: string]: DateRange | number[] | string;
    } {
      return {
        searchTerm: this.getQuickSearchValue,
        dateRange: this.getDateRange,
        duration: this.getCurrentDurationRange,
      };
    },

    searchBarIsFilled(state): boolean {
      return (
        Object.values(this.currentSearchItems).some((v) => v) ||
        state.enteredSearchTerm !== ""
      );
    },

    isSearchBarActive(state: State): boolean {
      return state.searchBarIsOpen;
    },

    isPalm(state: State): boolean {
      return state.isPalmDevice;
    },

    getLocale(state: State): string {
      return state.configuration.locale;
    },

    getTravelNumber(state: State): string {
      return state.submitted.travelNumber || "";
    },

    isRestoreModalOpen(state: State): boolean {
      return state.openRestoreModal;
    },
  },
});
